import { RESTRICTED_ADVERTISER_CATEGORIES, hasPermission, type AdvertiserCategory, type CreativeReviewState, type CreativeVersionView } from "@townsy/contracts";
import { isApiError } from "../api/client";

/**
 * Plain-language rules and messages for the advertiser area (ADR-0009): categories (two are
 * restricted until the founder sets the advertiser policy), review states, who may review, and
 * what to say when the server refuses something. Presentation only: the server decides.
 */

export const CATEGORY_LABELS: Record<AdvertiserCategory, string> = {
  retail: "Retail and shops",
  food_drink: "Food and drink",
  health_fitness: "Health and fitness",
  professional_services: "Professional services",
  software_web: "Software and web",
  technology: "Technology",
  finance: "Finance",
  travel: "Travel",
  pets: "Pets",
  arts_culture: "Arts and culture",
  education: "Education",
  home_services: "Home services",
  entertainment: "Entertainment",
  other: "Other",
  crypto_digital_assets: "Crypto and digital assets (restricted)",
  gambling_betting: "Gambling and betting (restricted)",
};

export function isRestrictedCategory(c: AdvertiserCategory | string | null | undefined): boolean {
  return Boolean(c && (RESTRICTED_ADVERTISER_CATEGORIES as readonly string[]).includes(c));
}

export const RESTRICTED_CATEGORY_NOTE =
  "Restricted category: you can keep this profile and submit designs, but approval waits for Townsy's advertiser policy, which has not been decided yet. Owning a store is not affected; your sign stays a neutral placeholder until a design can be approved.";

export const REVIEW_STATE_LABEL: Record<CreativeReviewState, string> = {
  pending: "Waiting for review",
  approved: "Approved",
  rejected: "Not approved",
  revoked: "Approval revoked",
};

export const REVIEW_STATE_TONE: Record<CreativeReviewState, "info" | "success" | "warning" | "danger"> = {
  pending: "info",
  approved: "success",
  rejected: "danger",
  revoked: "warning",
};

/**
 * May these roles use the design review queue (ADR-0009 #5)? The `creatives.review` permission of the shared matrix
 * (ADR-0035: moderator, admin). The server enforces it on every request.
 */
export function canReview(roles: readonly string[] | null | undefined): boolean {
  return hasPermission(roles, "creatives.review");
}

/** Library order: newest first, so the latest version of each design is on top. */
export function sortCreatives(items: readonly CreativeVersionView[]): CreativeVersionView[] {
  return [...items].sort((a, b) => (a.submittedAt < b.submittedAt ? 1 : a.submittedAt > b.submittedAt ? -1 : b.version - a.version));
}

export function approvedOfKind(items: readonly CreativeVersionView[], kind: CreativeVersionView["kind"]): CreativeVersionView[] {
  return sortCreatives(items.filter((c) => c.kind === kind && c.reviewState === "approved"));
}

// ---------------------------------------------------------------- server refusals

function common(err: unknown, fallback: string): string | null {
  if (!isApiError(err)) return "Couldn't reach Townsy. Check your connection and try again.";
  switch (err.code) {
    case "UNAUTHENTICATED":
      return "Sign in again to continue. Your session may have expired.";
    case "EMAIL_NOT_VERIFIED":
      return "Verify your email address first (see your Account page).";
    case "BANNED":
      return "Your account can't do this right now.";
    case "RATE_LIMITED":
      return "Too many changes in a short time. Wait a minute, then try again.";
    case "FEATURE_DISABLED":
      return "Advertiser tools are switched off right now.";
    case "DEPENDENCY_UNAVAILABLE":
    case "INTERNAL":
      return "Townsy couldn't save this right now. Try again in a moment.";
    case "VALIDATION_FAILED":
      return `${fallback} ${err.message ? `(${err.message})` : ""}`.trim();
    default:
      return null;
  }
}

export function profileErrorMessage(err: unknown): string {
  const c = common(err, "Check the business name (1-60 characters), description (up to 280) and category.");
  if (c) return c;
  if (isApiError(err) && (err.code === "FORBIDDEN" || err.status === 403)) return "Only the account that owns this advertiser profile can change it.";
  if (isApiError(err) && err.code === "NOT_FOUND") return "This advertiser profile could not be found.";
  return isApiError(err) && err.message ? err.message : "The profile could not be saved.";
}

export function creativeErrorMessage(err: unknown): string {
  const c = common(err, "Check the highlighted fields.");
  if (c) return c;
  if (isApiError(err)) {
    switch (err.code) {
      case "UPLOAD_REJECTED":
        return `The image was not accepted: ${err.message}`;
      case "CATEGORY_RESTRICTED":
        return "Your category is restricted: the design was not accepted for review until Townsy's advertiser policy allows it.";
      case "FORBIDDEN":
        return "This advertiser profile belongs to another account.";
      case "NOT_FOUND":
        return "The advertiser profile or the uploaded image could not be found. Upload the image again.";
    }
    if (err.message) return err.message;
  }
  return "The design could not be submitted.";
}

export function placementErrorMessage(err: unknown): string {
  const c = common(err, "The request was not accepted.");
  if (c) return c;
  if (isApiError(err)) {
    switch (err.code) {
      case "FORBIDDEN":
        return "Only the store's current owner can change its sign. If the store was taken over, you can no longer edit it.";
      case "CREATIVE_NOT_APPROVED":
        return "Only an approved store-sign design can be shown. This one is not approved (any more).";
      case "PROPERTY_VERSION_CHANGED":
        return "The store changed since you opened this (for example a new owner). Check the store again.";
      case "PROPERTY_TYPE_NOT_ELIGIBLE":
        return "Only stores have a business sign.";
      case "CATEGORY_RESTRICTED":
        return "Your category is restricted until Townsy's advertiser policy allows it.";
      case "NOT_FOUND":
        return "The store or the design could not be found.";
    }
    if (err.message) return err.message;
  }
  return "The sign could not be changed.";
}

export function reviewErrorMessage(err: unknown): string {
  const c = common(err, "The decision was not accepted.");
  if (c) return c;
  if (isApiError(err)) {
    switch (err.code) {
      case "FORBIDDEN":
        // Two causes (no reviewer role, or the reviewer's own design): the server's sentence says which.
        return err.message || "Only Townsy staff with the moderator or admin role can review designs.";
      case "CATEGORY_RESTRICTED":
        return "Approval is blocked: this advertiser's category is restricted until the advertiser policy allows it. You can reject it or leave it pending.";
      case "NOT_FOUND":
        return "This design is no longer in the queue.";
      case "IDEMPOTENCY_CONFLICT":
      case "PROPERTY_VERSION_CHANGED":
        return "Someone else decided on this design already. The queue has been refreshed.";
    }
    if (err.message) return err.message;
  }
  return "The decision could not be saved.";
}

export function loadErrorMessage(err: unknown, what: string): string {
  if (isApiError(err)) {
    if (err.status === 401 || err.code === "UNAUTHENTICATED") return `Sign in to see ${what}.`;
    if (err.status === 403 || err.code === "FORBIDDEN") return `Your account can't see ${what}.`;
    if (err.status === 404 || err.code === "NOT_FOUND") return `${what[0]!.toUpperCase()}${what.slice(1)} are not available on this server yet.`;
    if (err.code === "FEATURE_DISABLED") return `${what[0]!.toUpperCase()}${what.slice(1)} are switched off right now.`;
  }
  return `Couldn't load ${what}. ${isApiError(err) && err.message ? err.message : "Try again."}`;
}

/** Review decisions: a note is required to reject or revoke (ADR-0009 #5). */
export function decisionProblem(decision: "approve" | "reject" | "revoke", note: string, opts: { blockedByPolicy: boolean; state: CreativeReviewState }): string | null {
  const trimmed = note.trim();
  if (trimmed.length > 500) return "Keep the note under 500 characters.";
  if (decision === "approve") {
    if (opts.blockedByPolicy) return "Approval is blocked by the advertiser policy for this category.";
    if (opts.state !== "pending") return "Only a design waiting for review can be approved.";
    return null;
  }
  if (decision === "reject" && opts.state !== "pending") return "Only a design waiting for review can be rejected.";
  if (decision === "revoke" && opts.state !== "approved") return "Only an approved design can be revoked.";
  if (!trimmed) return `Write a note for the advertiser explaining why (required to ${decision}).`;
  return null;
}
